/**
 * ResponsiveToolbar Component
 * 
 * A wrapper component that automatically handles toolbar overflow.
 * Items that don't fit are moved to a "More" dropdown menu.
 * 
 * Usage:
 * <ResponsiveToolbar>
 *   <ResponsiveToolbar.Item priority={1}>
 *     <button>Always visible</button>
 *   </ResponsiveToolbar.Item>
 *   <ResponsiveToolbar.Item priority={10}>
 *     <button>Can overflow</button>
 *   </ResponsiveToolbar.Item>
 *   <ResponsiveToolbar.Spacer />
 *   <ResponsiveToolbar.Item priority={5}>
 *     <button>Medium priority</button>
 *   </ResponsiveToolbar.Item>
 * </ResponsiveToolbar>
 * 
 * Lower priority numbers = more important = hidden last
 */

import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  cloneElement,
  isValidElement,
} from 'react';
import { createPortal } from 'react-dom';
import { calculateDropdownPosition, closeAllDropdowns, CLOSE_ALL_DROPDOWNS_EVENT, FLOWCHART_PAN_START_EVENT } from '../utils/dropdownPositioning';

// DOM node → React children (and optional overflowRender) for the More menu.
// Index-matching Children.toArray breaks when Items are conditional/wrapped;
// the overflow math already has the real DOM nodes, so we key off those.
const overflowContentByEl = new WeakMap();

// Individual toolbar item wrapper
const ToolbarItem = ({ 
  children, 
  priority = 5, 
  id,
  label, // Label to show in overflow menu
  overflowRender, // Custom render function for overflow menu
}) => {
  const itemRef = useRef(null);

  useLayoutEffect(() => {
    const el = itemRef.current;
    if (!el) return undefined;
    overflowContentByEl.set(el, { children, overflowRender });
    return () => {
      overflowContentByEl.delete(el);
    };
  }, [children, overflowRender]);

  return (
    <div 
      ref={itemRef}
      className="toolbar-item" 
      data-toolbar-item 
      data-priority={priority}
      data-item-id={id}
      data-item-label={label}
    >
      {children}
    </div>
  );
};

// Spacer component
const ToolbarSpacer = () => (
  <div className="unified-toolbar__spacer" data-toolbar-spacer />
);

// More button icon
const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

const ResponsiveToolbar = ({ children, className = '' }) => {
  const toolbarRef = useRef(null);
  const [hiddenItems, setHiddenItems] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const menuRef = useRef(null);
  const moreButtonRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  // Calculate menu position with edge detection
  const updateMenuPosition = useCallback(() => {
    if (moreButtonRef.current) {
      const rect = moreButtonRef.current.getBoundingClientRect();
      const bottomMargin = 100;
      const maxHeight = window.innerHeight - rect.bottom - bottomMargin;
      
      // Use 'right' to align menu's right edge to button's right edge
      const rightOffset = window.innerWidth - rect.right;
      
      setMenuPosition({
        top: rect.bottom + 4,
        right: rightOffset,
        maxHeight: Math.max(200, maxHeight),
      });
    }
  }, []);

  // Handle menu toggle
  const handleMenuToggle = useCallback(() => {
    if (!isMenuOpen) {
      closeAllDropdowns(moreButtonRef.current); // Close any other open dropdowns first
      updateMenuPosition();
    }
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen, updateMenuPosition]);

  // Listen for global close events (when another dropdown opens or view zooms/pans)
  // BUT don't close if the event originated from within this menu (child dropdowns)
  useEffect(() => {
    const handleCloseAll = (e) => {
      // Don't close if the event came from within the overflow menu
      if (menuRef.current && e.detail?.sourceElement && menuRef.current.contains(e.detail.sourceElement)) {
        return;
      }
      setIsMenuOpen(false);
    };
    const handlePanStart = () => setIsMenuOpen(false);
    
    window.addEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    window.addEventListener(FLOWCHART_PAN_START_EVENT, handlePanStart);
    return () => {
      window.removeEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
      window.removeEventListener(FLOWCHART_PAN_START_EVENT, handlePanStart);
    };
  }, []);

  // Calculate which items should be hidden
  const calculateOverflow = useCallback(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    // Get all toolbar items
    const items = Array.from(toolbar.querySelectorAll('[data-toolbar-item]'));
    const spacer = toolbar.querySelector('[data-toolbar-spacer]');
    const moreButton = toolbar.querySelector('.toolbar-overflow');
    
    if (items.length === 0) {
      setHiddenItems([]);
      return;
    }

    // Reset all items to visible for measurement
    items.forEach(item => {
      item.style.display = '';
      item.style.visibility = 'visible';
      item.style.position = '';
    });

    // Get toolbar dimensions
    const toolbarRect = toolbar.getBoundingClientRect();
    const toolbarStyle = getComputedStyle(toolbar);
    const toolbarPadding = parseFloat(toolbarStyle.paddingLeft) + parseFloat(toolbarStyle.paddingRight);
    const availableWidth = toolbarRect.width - toolbarPadding;

    // Measure each item
    const itemData = items.map((item, index) => {
      const rect = item.getBoundingClientRect();
      const style = getComputedStyle(item);
      const marginLeft = parseFloat(style.marginLeft) || 0;
      const marginRight = parseFloat(style.marginRight) || 0;
      return {
        element: item,
        index,
        width: rect.width + marginLeft + marginRight,
        priority: parseInt(item.dataset.priority || '5', 10),
        id: item.dataset.itemId,
        label: item.dataset.itemLabel,
      };
    });

    // Calculate total width needed
    const moreButtonWidth = 70; // Approximate width of "More" button
    let totalWidth = 0;
    
    // First pass: calculate total width
    itemData.forEach(item => {
      totalWidth += item.width;
    });

    // If everything fits, show all
    if (totalWidth <= availableWidth) {
      setHiddenItems([]);
      return;
    }

    // Need to hide some items - sort by priority (higher priority = hide first)
    const sortedByPriority = [...itemData].sort((a, b) => b.priority - a.priority);
    
    // Calculate which items to hide
    const toHide = [];
    let currentWidth = totalWidth;

    for (const item of sortedByPriority) {
      if (currentWidth <= availableWidth - moreButtonWidth) {
        break;
      }
      toHide.push(item);
      currentWidth -= item.width;
    }

    // Hide the items
    toHide.forEach(item => {
      item.element.style.display = 'none';
    });

    // Update state with hidden items (preserve original order)
    const hiddenIndices = new Set(toHide.map(item => item.index));
    const hiddenInOrder = itemData
      .filter(item => hiddenIndices.has(item.index))
      .map(item => ({
        index: item.index,
        id: item.id,
        label: item.label,
        element: item.element,
      }));

    setHiddenItems(hiddenInOrder);
  }, []);

  // Set up resize observer
  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    const handleResize = () => {
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(calculateOverflow, 50);
    };

    // Initial calculation after render
    requestAnimationFrame(() => {
      calculateOverflow();
    });

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(toolbar);

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(resizeTimeoutRef.current);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateOverflow, children]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (e) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(e.target) &&
        moreButtonRef.current &&
        !moreButtonRef.current.contains(e.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  // Render hidden items in the overflow menu
  const renderOverflowItems = () => {
    if (hiddenItems.length === 0) return null;

    return hiddenItems.map((hidden) => {
      const entry = overflowContentByEl.get(hidden.element);
      if (!entry) return null;

      if (typeof entry.overflowRender === 'function') {
        return (
          <div key={hidden.index} className="toolbar-overflow__menu-item">
            {entry.overflowRender()}
          </div>
        );
      }

      const content = entry.children;
      const renderContent = (node, key) => {
        if (!isValidElement(node)) return null;
        return (
          <div key={key} className="toolbar-overflow__menu-item">
            {cloneElement(node)}
          </div>
        );
      };

      if (Array.isArray(content)) {
        return content.map((node, i) => renderContent(node, `${hidden.index}-${i}`));
      }
      return renderContent(content, hidden.index);
    });
  };

  return (
    <div ref={toolbarRef} className={`unified-toolbar ${className}`}>
      {children}
      
      {/* More button - only shown when items are hidden */}
      {hiddenItems.length > 0 && (
        <div className="toolbar-overflow">
          <button
            ref={moreButtonRef}
            className={`toolbar-btn toolbar-overflow__trigger ${isMenuOpen ? 'toolbar-overflow__trigger--open' : ''}`}
            onClick={handleMenuToggle}
            title={`${hiddenItems.length} more options`}
          >
            <span className="toolbar-btn__content">
              <span className="toolbar-btn__icon">
                <MoreIcon />
              </span>
              <span className="toolbar-btn__label">More</span>
            </span>
            <span className="toolbar-overflow__badge">{hiddenItems.length}</span>
          </button>
        </div>
      )}

      {/* Overflow menu - rendered via portal to escape toolbar overflow:hidden */}
      {isMenuOpen && menuPosition && createPortal(
        <div 
          ref={menuRef} 
          className="toolbar-overflow__menu"
          style={{
            position: 'fixed',
            top: menuPosition.top,
            right: menuPosition.right,
            maxHeight: menuPosition.maxHeight,
            overflowY: 'auto',
            zIndex: 50000,
          }}
        >
          <div className="toolbar-overflow__menu-header">
            More Options
          </div>
          <div className="toolbar-overflow__menu-items">
            {renderOverflowItems()}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Attach sub-components
ResponsiveToolbar.Item = ToolbarItem;
ResponsiveToolbar.Spacer = ToolbarSpacer;

export default ResponsiveToolbar;
